import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  DescriptionOutlined as DocIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import ApiService from '../../services/api.service';
import { portalFeaturesForSession } from '../../services/portal/portalFeatures.service';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { ProTableStats } from '../../components/pro-table';

// Tile buckets — same as the TC-facing report.
const TILES = [
  { key: 'TOTAL', label: 'Total Expiring', field: 'totalExpiring', color: 'primary.main' },
  { key: 'R0_30', label: '0 - 30 days', field: 'expiring0_30', color: 'warning.main' },
  { key: 'R31_60', label: '31 - 60 days', field: 'expiring31_60', color: 'info.main' },
  { key: 'R61_90', label: '61 - 90 days', field: 'expiring61_90', color: 'secondary.main' },
  { key: 'EXPIRED', label: 'Already Expired', field: 'alreadyExpired', color: 'error.main' }
];

const toISO = (d) => {
  const dd = new Date(d);
  dd.setHours(0, 0, 0, 0);
  return dd.toISOString().split('T')[0];
};

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (base, n) => {
  const d = new Date(base);
  d.setDate(d.getDate() + n);
  return d;
};

const rangeForTile = (key) => {
  const today = startOfToday();
  switch (key) {
    case 'TOTAL':   return { fromDate: toISO(today), toDate: null };
    case 'R0_30':   return { fromDate: toISO(today), toDate: toISO(addDays(today, 30)) };
    case 'R31_60':  return { fromDate: toISO(addDays(today, 31)), toDate: toISO(addDays(today, 60)) };
    case 'R61_90':  return { fromDate: toISO(addDays(today, 61)), toDate: toISO(addDays(today, 90)) };
    case 'EXPIRED': return { fromDate: null, toDate: toISO(addDays(today, -1)) };
    default:        return { fromDate: null, toDate: null };
  }
};

const prettyType = (t) => {
  if (!t) return '';
  return String(t).replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const daysUntil = (expiry) => {
  if (!expiry) return null;
  const e = new Date(expiry);
  if (isNaN(e.getTime())) return null;
  e.setHours(0, 0, 0, 0);
  return Math.round((e - startOfToday()) / (1000 * 60 * 60 * 24));
};

const PortalDocumentExpiry = ({ portalUser }) => {
  const theme = useTheme();
  const companyAllowed = portalFeaturesForSession(portalUser).EMPLOYEES === true;
  const [kind, setKind] = useState(() => (
    companyAllowed && new URLSearchParams(window.location.search).get('kind') === 'COMPANY' ? 'COMPANY' : 'EMPLOYEE'));
  const isCompany = kind === 'COMPANY';

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  // ?range=R0_30 (etc.) opens the report on that tile — used by the dashboard's expiry bar.
  const [activeTile, setActiveTile] = useState(() => {
    const r = new URLSearchParams(window.location.search).get('range');
    return TILES.some((t) => t.key === r) ? r : null;
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalElements, setTotalElements] = useState(0);

  const range = useMemo(() => rangeForTile(activeTile), [activeTile]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await ApiService.getPortalDocumentExpirySummary(isCompany ? { kind } : undefined);
      setSummary(res.data?.data || null);
    } catch (err) {
      console.error('Error loading document-expiry summary:', err);
      setSummary(null);
    }
  }, [isCompany, kind]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, size: rowsPerPage };
      if (isCompany) params.kind = kind;
      if (range.fromDate) params.fromDate = range.fromDate;
      if (range.toDate) params.toDate = range.toDate;
      const res = await ApiService.getPortalDocumentExpiry(params);
      const data = res.data?.data || {};
      setRows(Array.isArray(data.content) ? data.content : []);
      setTotalElements(data.totalElements ?? data.total_elements ?? 0);
    } catch (err) {
      console.error('Error loading document-expiry report:', err);
      setRows([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, range, isCompany, kind]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleKindChange = (e, next) => {
    setKind(next);
    setActiveTile(null);
    setPage(0);
  };

  const handleTileClick = (key) => {
    setActiveTile((prev) => (prev === key ? null : key));
    setPage(0);
  };

  // Filled chip coloured by how close the expiry is.
  const daysChip = (expiry) => {
    const d = daysUntil(expiry);
    if (d === null) return <Chip label="—" size="small" />;
    if (d < 0) return <Chip label={`Expired ${Math.abs(d)}d ago`} size="small" color="error" />;
    if (d <= 30) return <Chip label={`${d}d left`} size="small" color="warning" />;
    if (d <= 90) return <Chip label={`${d}d left`} size="small" color="info" />;
    return <Chip label={`${d}d left`} size="small" color="success" />;
  };

  const columns = isCompany
    ? ['Document Type', 'Doc Number', 'Expiry Date', 'Status']
    : ['Employee', 'Employee ID', 'Document Type', 'Doc Number', 'Issue Date', 'Expiry Date', 'Status'];

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {companyAllowed && (
        <Tabs value={kind} onChange={handleKindChange} sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Tab value="EMPLOYEE" label="Employee documents" />
          <Tab value="COMPANY" label="Company documents" />
        </Tabs>
      )}
      <ProTableStats
        loading={!summary && loading}
        cards={TILES.map((tile) => ({
          key: tile.key,
          label: tile.label,
          value: summary ? (summary[tile.field] ?? 0) : 0,
          color: tile.color,
          highlight: activeTile === tile.key,
          onClick: () => handleTileClick(tile.key)
        }))}
      />

      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mb: 1.5, minHeight: 32 }}>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap', mr: 1 }}>
          {totalElements} documents
        </Typography>
        <Tooltip title="Refresh">
          <IconButton
            size="small"
            onClick={() => { fetchSummary(); fetchRows(); }}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Table */}
      <Paper sx={{ overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={24} />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <DocIcon sx={{ fontSize: 40, color: 'action.disabled', mb: 1 }} />
            <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
              {activeTile ? 'No documents in this range' : 'No documents with an expiry date'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', mt: 0.5 }}>
              Your documents will appear here as they near expiry.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead sx={getBrandedTableHeadSx(theme)}>
                  <TableRow>
                    {columns.map((c) => (
                      <TableCell key={c} sx={{ whiteSpace: 'nowrap' }}>
                        {c}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isCompany && rows.map((r) => (
                    <TableRow key={r.document_group_id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.document_type_label || prettyType(r.document_type)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.document_number || '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(r.expiry_date)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{daysChip(r.expiry_date)}</TableCell>
                    </TableRow>
                  ))}
                  {!isCompany && rows.map((r) => {
                    const name = `${r.firstName || ''} ${r.lastName || ''}`.trim() || '—';
                    return (
                      <TableRow key={r.documentId} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{name}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.employeeId || '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{prettyType(r.documentType)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.documentNumber || '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(r.issueDate)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(r.expiryDate)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{daysChip(r.expiryDate)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalElements}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default PortalDocumentExpiry;
